/**
 * "Licensing": one row per creator in the video, shared by the export dialog and the ad pack. A client confirms its
 * side of a licence Beatly already confirmed, or asks Beatly to confirm; staff see the same rows read-only (Beatly's
 * side is confirmed on the campaign run page in the BO).
 */
import { useState } from 'react';
import { CheckCircle2, Clock3, Loader2, TriangleAlert } from 'lucide-react';
import { CoworkApiError } from '@/cowork/omniad/api/coworkApi';
import { canAskBeatly, licenceRows, type LicenceRow } from './licensing';
import { confirmLicence, dayText, requestLicence, withdrawLicence, type ClipRights, type Licence } from './videoClient';

/** "Asked 14:05" today, "Asked 1 Nov 2026, 14:05" before; '' when the time is not a time. */
function askedText(iso: string, now = new Date()): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const time = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  return d.toDateString() === now.toDateString() ? `Asked ${time}` : `Asked ${dayText(iso)}, ${time}`;
}

function errorText(e: unknown): string {
  if (e instanceof CoworkApiError && e.body.error === 'licence_waiting_beatly') return 'Beatly has not confirmed this licence yet.';
  if (e instanceof CoworkApiError && e.status === 403) return 'You cannot change this licence.';
  return 'Could not save. Try again.';
}

export function LicensingBox({ clips, client, alert = false, onLicence }: {
  clips: ClipRights[];
  client: boolean;
  /** The rows came from a refused export (409 rights). */
  alert?: boolean;
  /** A licence call answered: the caller updates its rights answer. */
  onLicence?: (licence: Licence) => void;
}) {
  const rows = licenceRows(clips);
  const [busy, setBusy] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [askedAt, setAskedAt] = useState<Record<string, string>>({});
  if (!rows.length) return null;

  async function act(row: LicenceRow, action: 'confirm' | 'withdraw' | 'request') {
    if (!row.campaignId || !row.creatorKey) return;
    setBusy(row.key);
    setErrors((e) => ({ ...e, [row.key]: '' }));
    try {
      const { licence } = action === 'confirm' ? await confirmLicence(row.campaignId, row.creatorKey)
        : action === 'withdraw' ? await withdrawLicence(row.campaignId, row.creatorKey)
        : await requestLicence(row.campaignId, row.creatorKey, row.label);
      if (action === 'request') setAskedAt((a) => ({ ...a, [row.key]: licence.requestedAt || new Date().toISOString() }));
      onLicence?.(licence);
    } catch (e) {
      setErrors((x) => ({ ...x, [row.key]: errorText(e) }));
    } finally { setBusy(null); }
  }

  const who = client ? 'your team' : 'the brand';
  return (
    <section aria-label="Licensing" role={alert ? 'alert' : undefined} className="flex flex-col rounded-xl border border-[#eaecf0]">
      <div className="flex flex-col px-3 pt-2.5">
        <span className="text-[13px] font-semibold text-[#101828]">Licensing</span>
        <span className="text-[12px] text-[#667085]">Creator clips run in ads once Beatly and {who} confirm the licence.</span>
      </div>
      <ul className="flex flex-col divide-y divide-[#eaecf0]">
        {rows.map((r) => {
          const working = busy === r.key;
          const until = r.until ? `until ${dayText(r.until)}` : 'with no end date';
          // The time of the last ask: this session's answer, else the rights answer.
          const askedIso = askedAt[r.key] || r.requestedAt;
          const asked = askedIso ? askedText(askedIso) : '';
          const canAsk = canAskBeatly({ ...r, requested: r.requested || !!askedIso, requestedAt: askedIso });
          const icon = r.state === 'cleared' ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#3b7d0b]" />
            : r.state === 'ended' || r.state === 'blocked' ? <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0 text-[#dc6803]" />
            : <Clock3 className="mt-0.5 h-4 w-4 shrink-0 text-[#98a2b3]" />;
          return (
            <li key={r.key} className="flex gap-2.5 px-3 py-2.5">
              {icon}
              <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span className="truncate text-[13px] font-semibold text-[#101828]">{r.label}<span className="font-normal text-[#667085]"> · {r.clips} clip{r.clips > 1 ? 's' : ''}</span></span>
                {r.state === 'cleared' && (
                  <>
                    <span className="text-[12px] text-[#475467]">{r.until ? `Licensed until ${dayText(r.until)}` : 'Licensed, no end date'}</span>
                    <span className="flex flex-wrap items-center gap-x-2 text-[12px] text-[#667085]">Confirmed by Beatly and {who}
                      {client && <button type="button" disabled={working} onClick={() => void act(r, 'withdraw')} className="text-[12px] font-semibold text-[#475467] underline-offset-2 hover:underline disabled:opacity-50">Withdraw</button>}
                    </span>
                  </>
                )}
                {r.state === 'confirm' && (client ? (
                  <label className="mt-0.5 flex items-start gap-2 text-[12.5px] text-[#344054]">
                    {working ? <Loader2 className="mt-0.5 h-4 w-4 shrink-0 animate-spin text-[#BE43FF]" /> : <input type="checkbox" checked={false} onChange={() => void act(r, 'confirm')} className="mt-0.5 h-4 w-4 shrink-0 accent-[#BE43FF]" />}
                    <span>We hold the licence for {r.label} {until}</span>
                  </label>
                ) : <span className="text-[12px] text-[#667085]">Beatly confirmed {until}. Waiting for the brand to confirm.</span>)}
                {r.state === 'waiting' && (
                  <span className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px] text-[#667085]">Waiting for Beatly to confirm the licence
                    {client && (asked || r.requested) && <span className="text-[#475467]">{asked || 'Beatly was asked'}</span>}
                    {client && canAsk && <button type="button" disabled={working} onClick={() => void act(r, 'request')} className="inline-flex items-center gap-1 rounded-lg border border-[#BE43FF] px-2 py-0.5 text-[12px] font-semibold text-[#BE43FF] hover:bg-[#faf0ff] disabled:opacity-50">{working && <Loader2 className="h-3 w-3 animate-spin" />}{r.requested || askedIso ? 'Ask again' : 'Ask Beatly'}</button>}
                  </span>
                )}
                {(r.state === 'ended' || r.state === 'blocked') && <span className="text-[12px] text-[#93370d]">{r.reason}</span>}
                {r.state === 'ended' && client && (asked || canAsk) && (
                  <span className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px] text-[#667085]">
                    {asked && <span className="text-[#475467]">{asked}</span>}
                    {canAsk && <button type="button" disabled={working} onClick={() => void act(r, 'request')} className="inline-flex items-center gap-1 rounded-lg border border-[#BE43FF] px-2 py-0.5 text-[12px] font-semibold text-[#BE43FF] hover:bg-[#faf0ff] disabled:opacity-50">{working && <Loader2 className="h-3 w-3 animate-spin" />}Ask Beatly to renew</button>}
                  </span>
                )}
                {errors[r.key] && <span role="status" className="text-[12px] text-[#b42318]">{errors[r.key]}</span>}
              </span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
